import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  CalendarClock,
  ClipboardList,
  Contact,
  FlaskConical,
  History,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  Sparkles,
  Truck,
  User as UserIcon,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { cn } from '@/lib/utils';
import { hasAnyRole } from '@/lib/permissions';
import type { Role } from '@/types/api';
import { Button } from '@/components/ui/button';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
  adminOnly?: boolean;
  /** Polozka viditelna (aktivni) jen pro uzivatele s alespon jednou z techto roli. */
  roles?: Role[];
  /** Polozka jen pro vyvojovy rezim — vykresli se jen kdyz backend bezi v Development (isDev z AuthContext). */
  devOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/operativy', label: 'Operativy', icon: ClipboardList },
  { to: '/material', label: 'Materiál', icon: Package },
  { to: '/mechanizace', label: 'Mechanizace', icon: Truck },
  { to: '/cinnosti', label: 'Činnosti', icon: Wrench },
  { to: '/zamestnanci', label: 'Zaměstnanci', icon: Contact },
  { to: '/zastupy', label: 'Zástupy', icon: CalendarClock, roles: ['TECHNIK_INT', 'SCHVALOVATEL', 'ADMIN'] },
  { to: '/uzivatele', label: 'Uživatelé', icon: Users, adminOnly: true },
  { to: '/audit', label: 'Audit', icon: History, adminOnly: true },
  { to: '/novinky', label: 'Novinky', icon: Sparkles },
  { to: '/profil', label: 'Profil', icon: UserIcon },
  { to: '/develop', label: 'Develop', icon: FlaskConical, adminOnly: true, devOnly: true },
];

export function Layout({ children }: { children: ReactNode }) {
  const { user, isAdmin, isDev, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  // Mobilni off-canvas drawer s navigaci. Na desktopu (lg+) je sidebar fixne
  // viditelny a tento stav se ignoruje (sidebar ma lg:translate-x-0).
  const [navOpen, setNavOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  // Po prechodu na jinou routu zavreme mobilni drawer (uzivatel klikl v menu).
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Kdyz je drawer otevreny, zamkneme scroll pozadi (jen na mobilu — na lg+
  // drawer stejne nepouzivame, ale lock je neskodny, protoze drawer tam neotvirame).
  useEffect(() => {
    if (!navOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [navOpen]);

  return (
    <div className="flex min-h-full">
      {/* Backdrop pod mobilnim drawerem (klik mimo = zavrit). Na lg+ skryty. */}
      {navOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          aria-hidden="true"
          onClick={() => setNavOpen(false)}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-[260px] max-w-[80vw] flex-col border-r bg-white transition-transform duration-200 lg:w-[220px] lg:max-w-none lg:translate-x-0',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center gap-2 border-b px-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <ClipboardList className="h-5 w-5" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold">Dom</div>
            <div className="text-xs text-muted-foreground">Denní operativa</div>
          </div>
          {/* Zavrit drawer — jen na mobilu. */}
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            className="ml-auto rounded-md p-1.5 text-muted-foreground hover:bg-muted lg:hidden"
            aria-label="Zavřít menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {NAV.filter((item) => !item.devOnly || isDev).map((item) => {
            // Polozky urcene pouze adminovi vykreslime jako disabled, kdyz
            // aktualni uzivatel nema roli ADMIN. Drzime je v DOM, aby bylo
            // navenek videt, ze existuji (a aby nebyla navigace "skackava").
            // devOnly polozky uz jsou vyfiltrovane vyse (skryte mimo Development backend).
            const disabled = (item.adminOnly && !isAdmin)
              || (item.roles !== undefined && !hasAnyRole(user, item.roles));
            if (disabled) {
              return (
                <span
                  key={item.to}
                  aria-disabled="true"
                  className="flex cursor-not-allowed items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground/50"
                  title="Pouze pro administrátora"
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </span>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setNavOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-foreground/80 hover:bg-muted hover:text-foreground',
                  )
                }
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t p-3 text-xs text-muted-foreground">
          {user?.strana === 'NASE_FIRMA' ? 'Green Gas DPB a.s.' : 'Externí firma'}
        </div>
      </aside>

      <div className="flex min-h-screen flex-1 flex-col lg:ml-[220px]">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-2 border-b bg-white px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-2">
            {/* Hamburger — otevira mobilni drawer; na lg+ skryty. */}
            <button
              type="button"
              onClick={() => setNavOpen(true)}
              className="-ml-1 rounded-md p-2 text-foreground hover:bg-muted lg:hidden"
              aria-label="Otevřít menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="truncate text-sm font-semibold sm:text-base">
              Denní operativa montáží
            </h1>
          </div>

          {/* User dropdown */}
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted sm:px-3"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                <UserIcon className="h-4 w-4" />
              </div>
              <span className="hidden max-w-[140px] truncate font-medium sm:inline">
                {user?.jmeno ?? '—'}
              </span>
            </button>
            {menuOpen && (
              <>
                {/* click-away overlay */}
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 z-20 mt-1 w-56 rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
                  <div className="px-3 py-2 text-xs text-muted-foreground">
                    <div className="font-medium text-foreground sm:hidden">{user?.jmeno ?? '—'}</div>
                    {user?.email}
                  </div>
                  <div className="my-1 h-px bg-muted" />
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate('/profil');
                    }}
                  >
                    <UserIcon className="h-4 w-4" />
                    Profil
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2"
                    onClick={() => {
                      setMenuOpen(false);
                      void handleLogout();
                    }}
                  >
                    <LogOut className="h-4 w-4" />
                    Odhlásit
                  </Button>
                </div>
              </>
            )}
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
}
